<template>
    <component :is="external || disabled ? 'a' : 'RouterLink'" v-bind="linkAttrs" :class="['nav-link', gapClass, { disabled }]">
        <i v-if="icon && iconPosition === 'left'" :class="['icon', icon]" :style="{ fontSize: iconSize }" aria-hidden="true" />

        <span class="nav-link-text" :class="[textSizeClass, textClass]">
            {{ text }}
        </span>

        <i v-if="icon && iconPosition === 'right'" :class="['icon', icon]" :style="{ fontSize: iconSize }" aria-hidden="true" />
    </component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { RouterLink } from "vue-router";
import type { LinkSize } from "@/types/navigation";
import type { IconPosition } from "@/types/icons";

export default defineComponent({
    components: {
        RouterLink,
    },
    name: "NavLink",
    props: {
        text: {
            type: String,
            default: "Link text",
        },
        size: {
            type: String as PropType<LinkSize>,
            default: "sm",
        },
        icon: String,
        iconPosition: {
            type: String as PropType<IconPosition>,
            default: "none",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        to: {
            type: String,
            default: "",
        },
        textClass: {
            type: String,
        },
        external: {
            type: Boolean,
            default: false,
        },
        customTarget: {
            type: String as PropType<string>,
            default: "",
        },
    },
    computed: {
        // Only bind what each branch needs: undefined attrs fall through RouterLink and wipe the href it renders
        linkAttrs(): Record<string, string> {
            if (this.disabled) {
                return {};
            }
            if (this.external) {
                return { href: this.to, target: this.customTarget || "_blank", rel: "noopener noreferrer" };
            }
            return this.customTarget ? { to: this.to, target: this.customTarget } : { to: this.to };
        },
        iconSize(): string {
            const sizes: Record<string, string> = {
                xs: "14px",
                sm: "16px",
                md: "18px",
                lg: "20px",
                xl: "24px",
                xxl: "28px",
            };
            return sizes[this.size] || "18px";
        },
        textSizeClass(): string {
            const sizes: Record<string, string> = {
                xs: "text-xs",
                sm: "text-sm",
                md: "text-md",
                lg: "text-lg",
                xl: "text-xl",
                xxl: "text-2xl",
            };
            return sizes[this.size] || "text-sm";
        },
        gapClass(): string {
            const gaps: Record<string, string> = {
                xs: "gap-xs",
                sm: "gap-sm",
                md: "gap-md",
                lg: "gap-md",
                xl: "gap-md",
                xxl: "gap-md",
            };
            return gaps[this.size] || "gap-md";
        },
    },
});
</script>

<style scoped>
@import "@/style/navigation.css";
</style>
